export const LOCAL_SALVAGE_BID_PATH = 'restwert-gebot';

export type LocalSalvageOfferState = 'Open' | 'Expired' | 'Cancelled';

export interface LocalSalvageOfferData {
  tenantContactAsString: string | null;
  plateNumber: string | null;
  vehicleType: string | null;
  manufacturer: string | null;
  model: string | null;
  subModel: string | null;
  engineType: string | null;
  bodyType: string | null;
  doorsCount: number | null;
  registrationDate: string | null;
  lastRegistrationDate: string | null;
  vin: string | null;
  kbaNumber: string | null;
  capacity: string | null;
  powerString: string | null;
  constructionYearMonthString: string | null;
  color: string | null;
  gearDriveType: string | null;
  nextInspectionString: string | null;
  pollutantClass: string | null;
  previousOwnersCount: number | null;
  seatsCount: number | null;
  paintType: string | null;
  condition: string | null;

  paintSum: number | null;
  wageSum: number | null;
  sparePartsCosts: number | null;
  costOfRepairNet: number | null;
  replacementValueGross: number | null;
  replacementValueTaxationType: string | null;

  vehicleLocation: string | null;
  registrationCountry: string | null;
  zip: string | null;
  readMileage: string | null;
  estimatedMileage: string | null;
  statedMileage: string | null;

  isAirbagTriggered: boolean;
  isNotReadyToDrive: boolean;
  isRegistrationDocumentMissing: boolean;
  isRegistrationDocumentInvalid: boolean;
  hasOdourPollution: boolean;
  isVatReportable: boolean;
  isReimport: boolean;
  isRightHandDriveVehicle: boolean;
  cannotBeRolled: boolean;
  hasFullServiceHistory: boolean;
  hasDeerDamage: boolean;
  hasFireDamage: boolean;
  isTheftReturn: boolean;
  hasDisposalVerification: boolean;
  hasGearboxRisk: boolean;
  hasGearboxDamage: boolean;
  hasHailDamage: boolean;
  hasEngineRisk: boolean;
  hasEngineDamage: boolean;
  hasWaterDamage: boolean;
  onlyVehicleParts: boolean;

  equipment: string | null;
  damageDescription: string | null;
  calculation: string | null;

  images: { description: string | null; base64EncodedContent: string }[];
}

export interface LocalSalvageBidder {
  name: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
}

export interface LocalSalvageBid {
  bidValue: number;
  dayOfBid: string;
  validUntil: string;
}

export interface LocalSalvageOffer {
  code: string;
  state: LocalSalvageOfferState;
  dueDate: string;
  hasPdf: boolean;
  bidder: LocalSalvageBidder;
  bid: LocalSalvageBid | null;
  data: LocalSalvageOfferData | null;
}

export interface LocalSalvageBidForm {
  bidValue: number;
  validUntil: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  email: string;
  phone: string;
}

export interface LocalSalvageBidFormModel {
  bidValue: number | null;
  validUntil: Date | null;
  name: string;
  street: string;
  zip: string;
  city: string;
  email: string;
  phone: string;
}

export const LOCAL_SALVAGE_BID_MAX_VALID_MONTHS = 3;
const DEFAULT_VALID_DAYS = 3;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function toCalendarDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function createLocalSalvageBidFormModel(
  bidder: LocalSalvageBidder | null,
  today: Date
): LocalSalvageBidFormModel {
  return {
    bidValue: null,
    validUntil: addDays(today, DEFAULT_VALID_DAYS),
    name: bidder?.name ?? '',
    street: bidder?.street ?? '',
    zip: bidder?.zip ?? '',
    city: bidder?.city ?? '',
    email: bidder?.email ?? '',
    phone: bidder?.phone ?? '',
  };
}

export function toLocalSalvageBidForm(
  model: LocalSalvageBidFormModel
): LocalSalvageBidForm {
  return {
    bidValue: Math.round(model.bidValue! * 100) / 100,
    validUntil: toCalendarDay(model.validUntil!),
    name: model.name.trim(),
    street: model.street.trim(),
    zip: model.zip.trim(),
    city: model.city.trim(),
    email: model.email.trim(),
    phone: model.phone.trim(),
  };
}

export type LocalSalvageBidPageState =
  'loading' | 'loaded' | 'notFound' | 'error';
